import React, {useState} from 'react';
import {TableLineLoader} from "../../front/loader/TableLineLoader";
import DateService from "../../../service/DateService";
import {NavLink} from "react-router-dom";
import {ApiService, filter, sort} from "../../../service/ApiService";
import {DeleteConfirme} from "../DeleteConfirme";
import {race} from "../../../models/race";
import {scrollTo} from "../../../service/ToolsService";
import {Controller, useForm} from "react-hook-form";

type FormDataType = {
  label?: string;
  rideDate?: string;
  participation?: string;
}

type RaceListProp = {
  race: race[],
  loading?: boolean,
  refresh: () => void,
  order: (s: string) => void,
  filter: (f: filter[]) => void
  sort: sort,
}

export const RaceList = ({race, loading, refresh, sort, order, filter}: RaceListProp) => {
  const [del, setDel] = useState(undefined as undefined | race);

  const participationOptions = [
    {field: 'not', label: 'aucune'},
    {field: 'subsidized', label: 'subventionnée'},
    {field: 'paid', label: 'payé'},
  ];

  const deleteRace = async (race: race) => {
    await ApiService.delete(`/race/${race.raceId}`);
    refresh();
  };

  const registration = (race: race) => {
    return !race.cancel &&
      (
        (race.startRegistration !== undefined) ||
        (race.endRegistration !== undefined) ||
        (race.participantLimit !== undefined)
      );
  }

  const {
    control,
    register,
    handleSubmit,
  } = useForm<FormDataType>({
    defaultValues: {
      label: undefined,
      rideDate: undefined,
      participation: undefined,
    }
  });

  const onSubmit = async (data: FormDataType) => {
    const filters: filter[] = []

    if ((data.label || undefined) !== undefined) {
      filters.push({
        op: 'like',
        field: 'label',
        value: `*${data.label}*`
      })
    }

    if ((data.rideDate || undefined) !== undefined) {
      filters.push({
        op: 'eq',
        field: 'rideDate',
        value: `${data.rideDate}`
      })
    }

    switch (data.participation || undefined) {
      case 'not':
        filters.push({
          op: 'eq',
          field: 'subsidized',
          value: `0`
        })
        filters.push({
          op: 'eq',
          field: 'paid',
          value: `0`
        })
        break;
      case 'subsidized':
        filters.push({
          op: 'eq',
          field: 'subsidized',
          value: `1`
        })
        break;
      case 'paid':
        filters.push({
          op: 'eq',
          field: 'paid',
          value: `1`
        })
        break;
    }

    filter(filters);
  }

  return (
    <>
      <form method="post" onSubmit={handleSubmit(onSubmit)}>
        <table className="table table-responsive table-hover border">
        <thead>
        <tr className="table-primary">
          <th className="w30px"></th>
          <th onClick={() => order('rideDate')} className="cursor-pointer">
              <span className={sort.by === 'rideDate' ? 'd-flex justify-content-between' : ''}>
                Date
                {sort.by === 'rideDate' && (<>
                  <i className={`me-2 fa-solid ${sort.direction === 'DESC' ? 'fa-sort-up' : 'fa-sort-down'}`} />
                </>)}
              </span>
          </th>
          <th onClick={() => order('label')} className="cursor-pointer">
              <span className={sort.by === 'label' ? 'd-flex justify-content-between' : ''}>
                Nom
                {sort.by === 'label' && (<>
                  <i className={`me-2 fa-solid ${sort.direction === 'DESC' ? 'fa-sort-up' : 'fa-sort-down'}`} />
                </>)}
              </span>
          </th>
          <th className="text-center w80px">Participants</th>
          <th className="text-center w80px">Description</th>
          <th className="text-center w180px">Participation du club</th>
          <th className="text-center w150px">Modification</th>
          <th className="w190px"></th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td></td>
          <td>
            <input type="date" className="form-control max-w150px me-3" placeholder="~" {...register('rideDate')} />
          </td>
          <td>
            <input type="text" className="form-control" placeholder="~" {...register('label')} />
          </td>
          <td colSpan={2}></td>
          <td>
            <Controller
              name="participation"
              control={control}
              defaultValue=""
              render={({field}) => (
                <select {...field} className="form-select">
                  <option value="">~</option>
                  {participationOptions.map((participationOption, k) => (
                    <option key={k} value={participationOption.field}>
                      {participationOption.label}
                    </option>
                  ))}
                </select>
              )}
            />
          </td>
          <td></td>
          <td className="text-center align-middle">
            <button type="submit" className="btn btn-sm btn-success me-1">ok</button>
          </td>
        </tr>
        {loading && (
          <tr>
            <td colSpan={8} className="pt-4 pb-4 ps-5 pe-5">
              <TableLineLoader/>
            </td>
          </tr>
        )}

        {!loading && race.map((r: race, i: number) => (
          <tr key={i} className={`align-middle ${r.cancel ? 'table-danger text-decoration-line-through' : ''}`}>
            <td>{r.ranking ? (<i className="fa-solid fa-flag-checkered"/>) : (<i className="fa-solid fa-person-biking"/>)}</td>
            <td>
              {DateService
                .toLocaleString(r.rideDate, {day: '2-digit', month: '2-digit', year: '2-digit'})
              }
            </td>
            <td>{r.label}</td>
            <td className="text-center">
              {(registration(r) || r.participants?.length !== 0) && (
                <NavLink to={`/admin/race/participant/${r.raceId}`} onClick={scrollTo} className="text-decoration-none hover-container p-2">
                  {r.participants?.length}
                  {r.participantLimit ? ` / ${r.participantLimit}` : ''}
                  <i className="fa-solid fa-pencil ms-1 icon-hover"/>
                </NavLink>
              )}
            </td>
            <td className="text-center">{r.description && (<i className="fa-regular fa-file-lines"/>)}</td>
            <td className="text-center">
              {r.paid && (<><i className="fa-solid fa-money-bill-wave" title="Payée par le club"/> Payée</>)}
              {r.subsidized && (<><i className="fa-solid fa-hand-holding-dollar" title="Subventionnée par le club"/> Subventionnée</>)}
            </td>
            <td className="text-center">
              {DateService
                .toLocaleString(r.updatedAt ?? r.createdAt, {day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false})
              }
            </td>
            <td className="text-end">
              <NavLink to={`/admin/race/edit/${r.raceId}`} onClick={scrollTo} className="btn btn-xs btn-primary me-1">
                <i className="fa-solid fa-pencil me-1"/> modifier
              </NavLink>
              <button className="btn btn-xs btn-danger" onClick={() => setDel(r)}><i className="fa-solid fa-xmark me-1"/>supprimer</button>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      </form>
      {del && (
        <DeleteConfirme confirme={(c: boolean) => {
          if (c) {
            deleteRace(del);
          }

          setDel(undefined);
        }}/>
      )}
    </>
  );
}
